// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import AdditionalVerificationScreenTemplate from '@front/templates/AdditionalVerificationScreenTemplate.html';
import TerceraBaseWindowTemplate from '@front/templates/TerceraBaseWindowTemplate.html';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { VerificationMode, getTitleFor2FA } from '@shared/commons/UtilsClasses/VerificationMode';
import { Resources } from '@shared/localizations/Resources';
import { ConectionResultEnum } from '@shared/utils/Connections/connection.types';
import { TerceraWindowBase } from './TerceraWindowBase';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
export class AdditionalVerificationScreen extends TerceraWindowBase {
    constructor() {
        super(...arguments);
        this.forceCloseOnLogout = false;
    }
    oninit() {
        super.oninit();
        this.on('okClick', this.okClick);
        this.on('cancelClick', this.cancelClick);
    }
    oncomplete() {
        super.oncomplete();
        this.center();
        void this.set('focused', true);
    }
    okClick() {
        const okCallBack = this.get('okCallBack');
        if (!isNullOrUndefined(okCallBack)) {
            okCallBack(this.get('verificationValue'));
        }
        this.close();
    }
    cancelClick() {
        const cancelCallBack = this.get('cancelCallBack');
        if (!isNullOrUndefined(cancelCallBack)) {
            cancelCallBack();
        }
        this.close();
    }
    // Close button (cross icon).
    onCloseButtonClick() {
        if (this.canCloseFromButton()) {
            this.cancelClick();
        }
    }
    static Show(initRespons, okCB, cancelCB, connectionParams) {
        const isNeedActivation = !!(!isNullOrUndefined(initRespons) && initRespons.connectReason === ConectionResultEnum.NeedActivation);
        let headerText = Resources.getResource('screen.additionalVerification.title');
        if (!isNullOrUndefined(initRespons)) {
            const verificationMode = (initRespons === null || initRespons === void 0 ? void 0 : initRespons.verificationModeCode) === VerificationMode.GoogleAuthenticator &&
                (initRespons === null || initRespons === void 0 ? void 0 : initRespons.needVerificationPassword) === true
                ? VerificationMode.None // #125618
                : initRespons.verificationModeCode;
            headerText = getTitleFor2FA(verificationMode);
        }
        headerText = isNeedActivation
            ? Resources.getResource('screen.additionalVerification.Need activation')
            : headerText;
        const innerText = isNeedActivation
            ? Resources.getResource('screen.additionalVerification.Need activation.comment')
            : !isNullOrUndefined(connectionParams)
                ? Resources.getResource('screen.additionalVerification.comment').replace('{1}', connectionParams.connectionName)
                : headerText; // "Connection ({1}) required additional verification:"
        const plHolder = isNeedActivation
            ? Resources.getResource('screen.additionalVerification.Need activation.watermark')
            : Resources.getResource('screen.additionalVerification.watermark');
        const verScreen = new AdditionalVerificationScreen();
        void verScreen.set({
            allowPreviewPassword: connectionParams === null || connectionParams === void 0 ? void 0 : connectionParams.allowPreviewPassword,
            isNeedActivation,
            okCallBack: okCB,
            cancelCallBack: cancelCB,
            okText: Resources.getResource('general.messageBox.ok'),
            cancelText: Resources.getResource('button.cancel'),
            header: headerText,
            reasonText: innerText,
            verificationValuePlaceholder: plHolder,
            verificationMode: initRespons === null || initRespons === void 0 ? void 0 : initRespons.verificationModeCode,
            needVerificationPassword: initRespons === null || initRespons === void 0 ? void 0 : initRespons.needVerificationPassword,
            userID: initRespons === null || initRespons === void 0 ? void 0 : initRespons.UserID
        });
        MainWindowManager.AddControl(verScreen);
        KeyEventProcessor.OnKeyDown.Subscribe(verScreen.onGlobalKeyDown, verScreen);
    }
    onGlobalKeyDown(code) {
        if (this.get('focused')) {
            if (code === KeyCode.ESC) {
                this.cancelClick();
            }
            else if (code === KeyCode.ENTER) {
                this.okClick();
            }
        }
    }
    onteardown() {
        KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
    }
}
TerceraWindowBase.extendWith(AdditionalVerificationScreen, {
    template: TerceraBaseWindowTemplate,
    data: () => ({
        resizable: false,
        showFooter: false,
        okText: 'Ok',
        cancelText: 'Cancel',
        userID: null,
        verificationMode: null,
        needVerificationPassword: false,
        verificationValue: '',
        verificationValuePlaceholder: '<Enter activation code from E-mail>',
        header: 'Need activation',
        reasonText: 'Please enter activation code (sent to your mail)',
        isNeedActivation: true,
        allowPreviewPassword: false,
        okCallBack: null,
        cancelCallBack: null
    }),
    computed: {
        showGoogleAuthenticatorScreen() {
            return (this.get('verificationMode') === VerificationMode.GoogleAuthenticator &&
                !this.get('needVerificationPassword'));
        }
    },
    partials: { bodyPartial: AdditionalVerificationScreenTemplate }
});
